import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import moment, { Moment } from 'moment';
import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
} from 'antd';
// API
import { UPDATE_CB_PROMO_CODE } from 'api/cbPromocodes/mutations';
// Types
import {
  UpdateCbPromoCode,
  UpdateCbPromoCodeVariables,
} from 'api/cbPromocodes/types/UpdateCbPromoCode';
import {
  UpdateCbPromoCodeInput,
  CbDiscountDuration,
  CbPromoCodeTargetInput,
} from 'api/graphql-global-types';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Constants
import { commonTimeFormat } from 'constants/global';
// Helpers
import { CbPromoCode } from 'helpers/cbPromoCodes/helpers';
import { formItemRules } from 'helpers/cbPromoCodes/form-item-rules';
import { CB_DISCOUNT_DURATION_OPTIONS } from 'helpers/cbPromoCodes/constants';
// Components
import TargetsSelect from '../TargetsSelect/TargetsSelect';

const { TextArea } = Input;

const formItemLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};

interface EditFormValues {
  isActive: boolean;
  startsAt?: Moment | null;
  expiresAt?: Moment | null;
  limitTotalUses: boolean;
  totalUses?: number | null;
  perAccountLimit: number;
  note?: string | null;
  maxPromoPerRedemption?: number | null;
  applicableTargets?: CbPromoCodeTargetInput[];
  duration?: CbDiscountDuration;
  durationMonths?: number | null;
}

export type EditCbPromoCodeModalProps = {
  promoCode: CbPromoCode;
  show: boolean;
  onCancel: () => void;
};

const toTargetInputs = (promoCode: CbPromoCode): CbPromoCodeTargetInput[] => {
  if (
    promoCode.__typename === 'CbSubscriptionDiscountPromoCode' ||
    promoCode.__typename === 'CbSubscriptionTrialPromoCode'
  ) {
    return (promoCode.applicableTargets || []).map(
      ({ product, recurrence }) => ({
        product,
        recurrence: recurrence || undefined,
      })
    );
  }

  return [];
};

const EditCbPromoCodeModal: React.FC<EditCbPromoCodeModalProps> = ({
  promoCode,
  show,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [limitTotalUses, setLimitTotalUses] = useState<boolean>(
    promoCode.totalUses != null
  );

  const isMatch = promoCode.__typename === 'CbCreditMatchPromoCode';
  const isDiscount = promoCode.__typename === 'CbSubscriptionDiscountPromoCode';
  const isSubscription =
    isDiscount || promoCode.__typename === 'CbSubscriptionTrialPromoCode';

  const [duration, setDuration] = useState<CbDiscountDuration | undefined>(
    promoCode.__typename === 'CbSubscriptionDiscountPromoCode'
      ? promoCode.discountDuration
      : undefined
  );

  const handleDurationChange = (value: CbDiscountDuration): void => {
    setDuration(value);
  };

  const [updateCbPromoCode, { loading }] = useMutation<
    UpdateCbPromoCode,
    UpdateCbPromoCodeVariables
  >(UPDATE_CB_PROMO_CODE);

  const initialValues: EditFormValues = {
    isActive: promoCode.isActive,
    startsAt: promoCode.startsAt ? moment(promoCode.startsAt) : null,
    expiresAt: promoCode.expiresAt ? moment(promoCode.expiresAt) : null,
    limitTotalUses: promoCode.totalUses != null,
    totalUses: promoCode.totalUses,
    perAccountLimit: promoCode.perAccountLimit,
    note: promoCode.note || '',
    maxPromoPerRedemption:
      promoCode.__typename === 'CbCreditMatchPromoCode'
        ? promoCode.maxPromoPerRedemption
        : null,
    applicableTargets: toTargetInputs(promoCode),
    duration:
      promoCode.__typename === 'CbSubscriptionDiscountPromoCode'
        ? promoCode.discountDuration
        : undefined,
    durationMonths:
      promoCode.__typename === 'CbSubscriptionDiscountPromoCode'
        ? promoCode.discountDurationMonths
        : null,
  };

  const handleSubmit = async (): Promise<void> => {
    const values = (await form.validateFields()) as EditFormValues;

    const input: UpdateCbPromoCodeInput = {
      id: promoCode.id,
      isActive: values.isActive,
      startsAt: values.startsAt ? values.startsAt.toISOString() : null,
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      totalUses: values.limitTotalUses ? values.totalUses : null,
      perAccountLimit: values.perAccountLimit,
      note: values.note?.trim() || null,
    };

    // Forward-only cap; only send when the admin actually set a value.
    if (isMatch && values.maxPromoPerRedemption) {
      input.creditMatch = {
        maxPromoPerRedemption: Number(values.maxPromoPerRedemption),
      };
    }

    // Re-targets/re-times future redemptions; empty array = applies to everything.
    if (isSubscription) {
      input.subscription = {
        applicableTargets: (values.applicableTargets || []).map(
          ({ product, recurrence }) => ({
            product,
            recurrence: recurrence || undefined,
          })
        ),
        ...(isDiscount && values.duration
          ? {
              duration: values.duration,
              durationMonths:
                values.duration === CbDiscountDuration.repeating
                  ? values.durationMonths
                  : undefined,
            }
          : {}),
      };
    }

    try {
      setSubmitting(true);
      await updateCbPromoCode({ variables: { input } });
      successNotification(`Promo code "${promoCode.code}" updated.`);
      onCancel();
    } catch (error) {
      errorNotification((error as Error)?.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Edit promo code "${promoCode.code}"`}
      destroyOnClose
      visible={show}
      width={640}
      okText="Save"
      cancelText="Cancel"
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={submitting || loading}
    >
      <Form
        form={form}
        layout="horizontal"
        name="EditCbPromoCodeForm"
        initialValues={initialValues}
        autoComplete="off"
        validateTrigger="onSubmit"
        {...formItemLayout}
      >
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item name="startsAt" label="Starts at">
          <DatePicker
            showTime={{ format: 'h:mmA', use12Hours: true }}
            format={commonTimeFormat}
          />
        </Form.Item>

        <Form.Item name="expiresAt" label="Expires at">
          <DatePicker
            showTime={{ format: 'h:mmA', use12Hours: true }}
            format={commonTimeFormat}
          />
        </Form.Item>

        <Form.Item label="Total uses">
          <Space size="small" align="start">
            <Form.Item name="totalUses" noStyle>
              <InputNumber min={1} disabled={!limitTotalUses} />
            </Form.Item>
            <Form.Item name="limitTotalUses" valuePropName="checked" noStyle>
              <Checkbox
                onChange={() => {
                  setLimitTotalUses((prev) => !prev);
                  form.resetFields(['totalUses']);
                }}
              >
                Limit
              </Checkbox>
            </Form.Item>
          </Space>
        </Form.Item>

        <Form.Item
          name="perAccountLimit"
          label="Per-account limit"
          rules={[
            { required: true, message: 'Please enter a per-account limit' },
            { type: 'number', min: 1, message: 'Must be at least 1' },
          ]}
        >
          <InputNumber min={1} />
        </Form.Item>

        {isMatch && (
          <Form.Item
            name="maxPromoPerRedemption"
            label="Per-redemption cap"
            tooltip="Forward-only — past grants stay as recorded. Leave blank to keep uncapped."
          >
            <InputNumber min={0.01} step={0.01} precision={2} />
          </Form.Item>
        )}

        {isDiscount && (
          <>
            <Form.Item
              name="duration"
              label="Duration"
              tooltip="Re-times future redemptions; past ones keep their already-minted coupon. 'Repeating' counts calendar months, not billing periods — on a yearly price any repeating duration below 12 months is identical to 'once'."
              rules={formItemRules.duration}
            >
              <Select
                options={CB_DISCOUNT_DURATION_OPTIONS}
                onChange={handleDurationChange}
                style={{ maxWidth: 200 }}
              />
            </Form.Item>

            {duration === CbDiscountDuration.repeating && (
              <Form.Item
                name="durationMonths"
                label="Duration (months)"
                tooltip="Calendar months, not billing periods. On a yearly price any value below 12 discounts the whole first annual invoice — identical to 'once'."
                rules={formItemRules.durationMonths}
              >
                <InputNumber min={1} />
              </Form.Item>
            )}
          </>
        )}

        {isSubscription && (
          <Form.Item
            name="applicableTargets"
            label="Applies to"
            tooltip="Re-targets future redemptions; past ones keep their already-minted coupon. No targets = applies to everything."
            rules={formItemRules.applicableTargets}
          >
            <TargetsSelect />
          </Form.Item>
        )}

        <Form.Item name="note" label="Internal note">
          <TextArea rows={2} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCbPromoCodeModal;
